import { faFileInvoice, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface SelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onInvoice?: () => void;
  isInvoicing?: boolean;
}

const ActionButton = memo(
  ({
    icon,
    label,
    onClick,
    disabled,
  }: {
    icon: typeof faFileInvoice;
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="border-card-border bg-transparent"
      data-testid="selection-action-invoice-minimum-fees"
    >
      <FontAwesomeIcon icon={icon} className="w-3 h-3 mr-2" />
      {label}
    </Button>
  ),
);

ActionButton.displayName = 'ActionButton';

export const SelectionToolbar = memo(
  ({ selectedCount, onClose, onInvoice, isInvoicing = false }: SelectionToolbarProps) => {
    const { t } = useTranslation('minimumFeesModule');

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    const handleInvoice = useCallback(() => {
      onInvoice?.();
    }, [onInvoice]);

    if (selectedCount === 0) {
      return null;
    }

    return (
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 bg-primary text-btn-text text-sm rounded-full flex items-center justify-center">
                {selectedCount}
              </span>
              <span className="text-sm font-medium">{t('selection.selected', 'Seleccionados')}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="p-0 hover:bg-transparent ml-1"
                data-testid="clear-selection-button"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <ActionButton
                icon={faFileInvoice}
                label={
                  isInvoicing
                    ? t('actions.invoicing', 'Facturando...')
                    : t('actions.invoiceSelected', 'Facturar cuotas mínimas')
                }
                onClick={handleInvoice}
                disabled={isInvoicing}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

SelectionToolbar.displayName = 'SelectionToolbar';
